/* eslint-disable */
import { useCallback, useEffect, useMemo, useState } from 'react';

const formatDualValue = (value) => {
	if (typeof value === 'string' && value.includes('/')) {
		const parts = value.split('/');
		if (parts.length === 2) {
			let numerator = parts[0].trim();
			if (!isNaN(parseFloat(numerator))) {
				const denominatorPart = parts[1].trim();
				const lastSpaceIndex = denominatorPart.lastIndexOf(' ');
				let denominator = denominatorPart;
				let unit = '';

				if (lastSpaceIndex !== -1) {
					denominator = denominatorPart.substring(0, lastSpaceIndex);
					unit = denominatorPart.substring(lastSpaceIndex + 1);
				}

				return (
					<div className='inline-flex items-center gap-1.5 px-1 py-0.5 rounded hover:bg-gray-50/50 transition-colors'>
						<div className='flex flex-col items-center justify-center min-w-[20px]'>
							<span className='border-b border-current text-[11px] font-semibold leading-none pb-[1px] w-full text-center tracking-tight'>
								{numerator}
							</span>
							<span className='text-[11px] font-medium leading-none pt-[1px] w-full text-center tracking-tight opacity-80'>
								{denominator}
							</span>
						</div>
						{unit && (
							<span className='text-[10px] text-gray-400 font-semibold uppercase tracking-wide self-center'>
								{unit}
							</span>
						)}
					</div>
				);
			}
		}
	}
	return value;
};

export const useMasterTableData = (rawData, activeTab, search, isLoading) => {
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);

	useEffect(() => {
		if (isLoading) {
			setColumns([]);
			setRows([]);
		}
	}, [isLoading]);

	useEffect(() => {
		if (rawData?.columns?.length > 0 || rawData?.data?.length > 0) {
			setColumns(rawData?.columns);
			setRows(rawData?.data);
		}
	}, [rawData]);

	const getColumns = useMemo(() => {
		if (!columns?.length) return [];
		return columns.map((column) => ({
			...column,
			title: column?.label,
			searchable:
				column?.key === 'productName' || column?.key === 'name'
					? true
					: column?.searchable,
			customCol: true,
		}));
	}, [columns]);

	const filterData = useMemo(() => {
		if (!rows?.length) return [];
		if (!search?.searchValue) return rows;

		const searchLower = String(search?.searchValue).toLowerCase().trim();

		return rows.filter((item) => {
			const searchableText = Object.values(item)
				.map((value) => {
					if (value === null || value === undefined) return '';

					if (typeof value === 'object' && value !== null) {
						if ('value' in value) {
							return String(value.value || '');
						}
						return JSON.stringify(value);
					}

					return String(value);
				})
				.filter(Boolean)
				.join(' ')
				.toLowerCase();

			return searchableText.includes(searchLower);
		});
	}, [rows, search?.searchValue, search?.value]);

	const customRowData = useMemo(() => {
		const base = {};
		getColumns.forEach((column, index) => {
			base[column.key] = (product) => {
				const cellData = product?.[column.key];

				if (
					cellData &&
					typeof cellData === 'object' &&
					cellData.value !== undefined
				) {
					const color = cellData.extra?.color;
					const colorClass =
						color === 'red'
							? 'text-red-600'
							: color === 'green'
							? 'text-green-600'
							: color === 'yellow'
							? 'text-yellow-600'
							: 'text-gray-700';
					return (
						<div
							className={`text-left text-[12px] ${
								cellData?.link
									? 'cursor-pointer hover:underline text-blue-700'
									: colorClass
							}`}
							{...(cellData?.link
								? {
										onClick: () =>
											window.open(
												cellData?.link,
												'_blank'
											),
								  }
								: {})}
						>
							{formatDualValue(cellData.value ?? '-')}
						</div>
					);
				}
				return (
					<div className='text-gray-700 text-left text-[12px]'>
						{formatDualValue(cellData ?? '-')}
					</div>
				);
			};
		});
		return base;
	}, [getColumns, search]);

	const sortInfo = useMemo(() => {
		const info = {};
		getColumns.forEach((column) => {
			info[column.key] = {
				type: column?.dataType,
				customValue: true,
				value: (obj) => {
					const cellData = obj?.[column.key];
					let value = '';
					if (
						cellData &&
						typeof cellData === 'object' &&
						cellData.value !== undefined
					) {
						value = cellData?.value;
					} else {
						value = cellData;
					}
					return value;
				},
			};
		});
		return info;
	}, [getColumns]);

	const customCol = useCallback((column) => {
		return (
			<div className='flex flex-col gap-1'>
				<div className='flex items-center gap-2'>
					<p
						className={`text-left text-[11px] text-nowrap ${
							column?.link
								? 'cursor-pointer hover:underline text-blue-700'
								: 'text-gray-700'
						}`}
						{...(column?.link
							? {
									onClick: () =>
										window.open(column?.link, '_blank'),
							  }
							: {})}
					>
						{column?.label ?? ''}
					</p>
				</div>
				{Array.isArray(column?.data) &&
					column?.data?.length > 0 &&
					column?.data?.map((item, idx) => (
						<div key={idx} className='flex flex-col'>
							<p
								className={`flex gap-2 justify-start m-0 text-nowrap ${
									item?.link
										? 'cursor-pointer hover:underline text-blue-700'
										: 'text-gray-700'
								}`}
								{...(item?.link
									? {
											onClick: () =>
												window.open(
													item?.link,
													'_blank'
												),
									  }
									: {})}
							>
								<span className='text-[11px]'>
									{item?.label}
								</span>{' '}
								:{' '}
								<span className='text-[11px]'>
									{item?.value}
								</span>
							</p>
						</div>
					))}
			</div>
		);
	}, []);

	const getTableInfo = useCallback(() => {
		return {
			data: filterData,
			tableRows: customRowData,
			tableColumns: getColumns,
			sortInfo,
			customCol,
		};
	}, [rows, getColumns, customRowData, sortInfo, search, customCol]);

	const dropDownItems = useMemo(() => {
		if (!getColumns.length) return [];
		return getColumns
			.filter((column) => column?.searchable)
			.map((column) => ({
				label: column?.label,
				value: column?.key,
				placeholder: `Search by ${column?.label}`,
			}));
	}, [getColumns]);

	return {
		getTableInfo,
		dropDownItems,
	};
};
